import { Box, Button, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import CustomCheckbox from "@/components/core/mui-custom-components/checkbox-custom";
import React, { useState } from "react";
import Link from "@/components/core/localized-link";
import { useAlertStore } from "@/components/context/alert-store";
import { useSession } from "next-auth/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import LoginToSaveProduct from "@/components/core/alert-messages/login-warning";
import AddToCartSuccessMessage from "@/components/core/alert-messages/add-cart-success";
import Cookies from "js-cookie";
import useLocalizedRouter from "@/hooks/use-localized-router";
import querystring from "querystring";
import { sendGAEventTrigger } from "@/utils/analytics-trigger";
import { postRequest } from "@/utils/request";
import { useProductStore } from "@/components/context/product-store";
import { useShallow } from "zustand/shallow";
import XNoticeContent from "@/main/_components/xnotice-content/xnotice-content";
import { IOfferzBuyOptions, IOfferzOptions, IProduct } from "@/types/product";

interface ProductActionsProps {
  product: IProduct;
  offerzOptions: IOfferzOptions;
  lang: string;
}

const ProductActions = ({ product, offerzOptions, lang }: ProductActionsProps) => {
  const [terms, setTerms] = useState<boolean>(false);
  const { setMessage, showUnexpectedErrorMessage, showTooManyAttemptsMessage } = useAlertStore();
  const { status } = useSession();
  const mutation = useMutation({
    mutationFn: (data: object) => postRequest({ path: { name: "cart" }, body: data, lang }),
  });
  const router = useLocalizedRouter();
  const queryClient = useQueryClient();

  const { qty, inputValues, error, setApiErrors } = useProductStore(
    useShallow((state) => ({
      qty: state.qty,
      inputValues: state.inputValues,
      error: state.error,
      setApiErrors: state.setApiErrors,
    })),
  );

  const cookieKey = status === "unauthenticated" ? "anon-wishlist" : "wishlist";
  const wishlistRaw = Cookies.get(cookieKey);
  let wishlistArray: string[] = [];

  if (wishlistRaw) {
    try {
      wishlistArray = JSON.parse(wishlistRaw);
    } catch {
      wishlistArray = [];
    }
  }
  const productId = product.id.toString();
  const addedToWishlist = wishlistArray.some((id: string) => id === productId);

  const [isAddingToCart, setIsAddingToCart] = useState(false);

  const addToWishlistMutation = useMutation({
    mutationFn: () =>
      postRequest({ path: { name: "wishlist", id: String(product.id) }, body: {}, lang }),
    onSuccess: (data) => {
      if (data.success === 0) {
        if (data.tooManyAttempts) {
          showTooManyAttemptsMessage();
          return;
        }
        showUnexpectedErrorMessage();
        return;
      }
      setMessage({
        text: (
          <Trans>
            Success: You have added{" "}
            <Link
              prefetch={false}
              href={`/product/${product.seo_url}`}
              style={{ color: "#19a1b3", textDecoration: "underline" }}
            >
              {product.name}
            </Link>{" "}
            to your{" "}
            <Link
              prefetch={false}
              href={"/account/wishlist"}
              style={{ color: "#19a1b3", textDecoration: "underline" }}
            >
              wishlist
            </Link>
            !
          </Trans>
        ),
        severity: "success",
      });
    },
    onError: () => {
      showUnexpectedErrorMessage();
    },
  });

  const removeFromWishlistMutation = useMutation({
    mutationFn: () =>
      postRequest({
        path: { name: "wishlist", id: String(productId) },
        body: {},
        method: "DELETE",
        lang,
      }),
    onSuccess: () => {
      setMessage({
        text: (
          <Trans>
            Success: You have deleted{" "}
            <Link
              prefetch={false}
              href={`/product/${product.seo_url}`}
              style={{ color: "#19a1b3", textDecoration: "underline" }}
            >
              {product.name}
            </Link>{" "}
            from your{" "}
            <Link
              prefetch={false}
              href={"/account/wishlist"}
              style={{ color: "#19a1b3", textDecoration: "underline" }}
            >
              wishlist
            </Link>
            !
          </Trans>
        ),
        severity: "success",
      });
    },
    onError: () => {
      showUnexpectedErrorMessage();
    },
  });

  const termsAndCond = offerzOptions?.terms?.tc_link ? (
    <Link href={offerzOptions?.terms?.tc_link} prefetch={false}>
      <Typography
        variant="body2"
        component="span"
        sx={{
          cursor: "pointer",
          px: "4px",
          "&:hover": {
            color: "primary.main",
          },
        }}
      >
        {offerzOptions?.terms?.tc_text}
      </Typography>
    </Link>
  ) : (
    <Typography
      variant="body2"
      component="span"
      sx={{
        cursor: "pointer",
        px: "4px",
      }}
    >
      {offerzOptions?.terms?.tc_text}
    </Typography>
  );

  const handleActionClick = (actionType: "buy" | "gift", e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (offerzOptions?.terms?.tc_required && !terms) {
      setMessage({
        text: t`You must accept the terms and conditions.`,
        severity: "warning",
      });
      return;
    }

    setMessage(null);

    if (error) return;

    const requiredOptions = offerzOptions?.buy_options
      .filter((option) => option.required === 1)
      .reduce<Record<string, unknown>>((acc, option: IOfferzBuyOptions) => {
        acc[option.product_option_id] = inputValues?.[option.product_option_id];
        return acc;
      }, {});

    if (actionType === "buy") {
      setIsAddingToCart(true);

      const payload = {
        product_id: product.id,
        quantity: qty,
        option: requiredOptions,
        ...(offerzOptions?.terms?.tc_required && { tc_accepted: terms }),
      };

      mutation.mutate(payload, {
        onError: (error: Error) => {
          console.error(error);
          setIsAddingToCart(false);
          showUnexpectedErrorMessage();
        },
        onSuccess: (data) => {
          setIsAddingToCart(false);

          if (data.success === 1) {
            setMessage({
              text: (
                <AddToCartSuccessMessage item={{ seo_url: product.seo_url, name: product.name }} />
              ),
              severity: "success",
            });
            queryClient.invalidateQueries({ queryKey: ["cartQuantity"] });
            sendGAEventTrigger("Add To Cart", {
              send_to: `${process.env.NEXT_PUBLIC_GTM_MEASUREMENT_ID}`,
              category: "ecommerce",
              label: product.name,
            });
            setApiErrors([]);
            return;
          }

          if (data.tooManyAttempts) {
            showTooManyAttemptsMessage();
            return;
          }

          if (!data.error_with_key) {
            setMessage({
              text: t`${
                data.error?.option
                  ? data.error.option.option[Object.keys(data.error.option.option)[0]]
                  : (data.error?.[0] ?? t`An unexpected error occurred. Please try again later.`)
              }`,
              severity: "error",
            });
          } else {
            setApiErrors(data.error_with_key);
          }
        },
      });
    } else {
      let properties;
      if (
        requiredOptions &&
        typeof requiredOptions === "object" &&
        Object.keys(requiredOptions).length === 0
      ) {
        properties = {
          product: product.id,
          quantity: qty,
        };
      } else {
        properties = {
          product: product.id,
          quantity: qty,
          option: JSON.stringify(requiredOptions),
        };
      }
      router.push(`/create-gift?${querystring.stringify(properties)}`);
    }
  };

  const handleWishlistClick = async (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    if (status === "unauthenticated") {
      setMessage({
        text: <LoginToSaveProduct item={{ id: productId, name: product.name }} />,
        severity: "warning",
      });
    }
    const addedToWishlist = wishlistArray.some((wishlistItem: any) => wishlistItem === productId);
    if (addedToWishlist) {
      if (status !== "unauthenticated") {
        removeFromWishlistMutation.mutate();
      }
      wishlistArray = wishlistArray.filter((id: string) => id !== productId);
    } else {
      if (status !== "unauthenticated") {
        addToWishlistMutation.mutate();
      }
      sendGAEventTrigger("Add To Wishlist", {
        send_to: `${process.env.NEXT_PUBLIC_GTM_MEASUREMENT_ID}`,
        label: product.name,
        category: "ecommerce",
      });
      wishlistArray.push(productId);
    }

    if (status === "unauthenticated") {
      Cookies.set("anon-wishlist", JSON.stringify(wishlistArray));
    } else {
      Cookies.set("wishlist", JSON.stringify(wishlistArray));
    }
  };

  return (
    <>
      {offerzOptions?.terms?.tc_required && (
        <Box sx={{ pt: 4 }}>
          <CustomCheckbox
            name="terms"
            labelComponents={termsAndCond}
            checked={terms}
            onChange={() => setTerms(!terms)}
            data-testid="product-terms"
          />
        </Box>
      )}
      <div className="mt-[30px]">
        <XNoticeContent settings={{ selector: ".product-actions" }} />
      </div>

      <Box sx={{ display: "flex", gap: 1, mt: "30px", width: "100%" }}>
        <Box
          sx={{
            width: product.offerz_show_gift_button ? "100%" : { xs: "100%", md: "50%" },
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 1,
          }}
        >
          <Button
            type="button"
            data-testid="product-buy"
            variant="contained"
            disabled={isAddingToCart}
            sx={{ width: "100%", fontSize: "17px", fontWeight: 400 }}
            onClick={(e) => handleActionClick("buy", e)}
          >
            {isAddingToCart ? <CircularProgress size={24} /> : <Trans>Buy</Trans>}
          </Button>
          {product.offerz_show_gift_button && (
            <Button
              data-testid="product-gift"
              sx={{
                width: "100%",
                bgcolor: "primary.light",
                fontSize: "17px",
                fontWeight: 400,
                "&:hover": {
                  bgcolor: "primary.light",
                },
              }}
              onClick={() => handleActionClick("gift")}
            >
              <Trans>Make a gift</Trans>
            </Button>
          )}
        </Box>

        <Box sx={{ display: { xs: "none", md: "block" } }}>
          <Tooltip
            arrow
            placement="top"
            title={addedToWishlist ? t`Remove from wish list` : t`Add to wish list`}
          >
            <IconButton
              onClick={handleWishlistClick}
              data-testid="product-wishlist"
              disableRipple
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "100%",
                bgcolor: "rgb(246, 246, 247)",
                width: "50px",
                height: "50px",
                minWidth: "auto",
                color: addedToWishlist ? "primary.main" : "#000",
                "&:hover": {
                  bgcolor: "primary.main",
                  color: "#fff",
                },
              }}
            >
              {addedToWishlist ? <FavoriteIcon /> : <FavoriteBorderIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </>
  );
};

export default React.memo(ProductActions);
